import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { getToken } from '../../../features/token/tokenSaver';
import { getAdminLegalDocuments } from '../../../services/getAdminLegalDocuments';
import { updateLegalDocument } from '../../../services/updateLegalDocument';
import 'react-tabs/style/react-tabs.css';
import '../AdminDashboard/AdminDashboard.css';
import './AdminLegalDocuments.css';

const TAB_CONFIG = [
    { key: 'advertiser_tos', label: 'Advertiser Terms of Service', hasEsign: false },
    {
        key: 'advertiser_platform_agreement',
        label: 'Network Advertising Agreement (all screens)',
        hasEsign: true
    },
    { key: 'host_agreement', label: 'Screen Host Agreement', hasEsign: true }
];

function formatUpdatedAt(iso) {
    if (!iso) return 'Not yet saved to database (using defaults)';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

function DocumentEditor({ documentKey, label, hasEsign, doc, onSaved }) {
    const token = useSelector(getToken);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [esignNotice, setEsignNotice] = useState('');
    const [savedSnapshot, setSavedSnapshot] = useState(null);
    const [viewMode, setViewMode] = useState('edit');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (!doc) return;
        const snapshot = {
            title: doc.title || '',
            body: doc.body || '',
            esignNotice: doc.esignNotice || ''
        };
        setTitle(snapshot.title);
        setBody(snapshot.body);
        setEsignNotice(snapshot.esignNotice);
        setSavedSnapshot(snapshot);
    }, [doc]);

    const isDirty = useMemo(() => {
        if (!savedSnapshot) return false;
        return (
            title !== savedSnapshot.title ||
            body !== savedSnapshot.body ||
            esignNotice !== savedSnapshot.esignNotice
        );
    }, [title, body, esignNotice, savedSnapshot]);

    const handleDiscard = useCallback(() => {
        if (!savedSnapshot) return;
        setTitle(savedSnapshot.title);
        setBody(savedSnapshot.body);
        setEsignNotice(savedSnapshot.esignNotice);
        setError('');
        setSuccess('');
    }, [savedSnapshot]);

    const handleSave = useCallback(() => {
        if (!body.trim()) {
            setError('Document body cannot be empty.');
            return;
        }
        setSaving(true);
        setError('');
        setSuccess('');
        const payload = {
            documentKey,
            title: title.trim(),
            body: body.trim()
        };
        if (hasEsign) {
            payload.esignNotice = esignNotice.trim();
        }
        updateLegalDocument(token, payload)
            .then((res) => {
                if (res.data?.success && res.data.document) {
                    const updated = res.data.document;
                    const snapshot = {
                        title: updated.title || '',
                        body: updated.body || '',
                        esignNotice: updated.esignNotice || ''
                    };
                    setSavedSnapshot(snapshot);
                    setTitle(snapshot.title);
                    setBody(snapshot.body);
                    setEsignNotice(snapshot.esignNotice);
                    setSuccess('Saved. Version bumped to ' + updated.version + '.');
                    onSaved(updated);
                } else {
                    setError('Save failed.');
                }
            })
            .catch((err) => {
                const msg = err.response?.data?.error || 'Save failed.';
                setError(msg);
            })
            .finally(() => {
                setSaving(false);
            });
    }, [token, documentKey, title, body, esignNotice, hasEsign, onSaved]);

    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    if (!doc) {
        return <div className="admin-legal-loading">Loading…</div>;
    }

    const layoutClass = [
        'admin-legal-editor-layout',
        viewMode === 'preview' ? 'preview-only' : '',
        viewMode === 'edit' ? 'edit-only' : ''
    ].filter(Boolean).join(' ');

    return (
        <div>
            <div className="admin-legal-meta">
                Version <strong>{doc.version}</strong>
                {' · '}
                Updated {formatUpdatedAt(doc.updatedAt)}
                {doc.isDefault && ' · Showing built-in defaults until first save'}
            </div>

            <div className="admin-legal-toolbar">
                <div className="admin-legal-view-toggle">
                    <button
                        type="button"
                        className={viewMode === 'edit' ? 'active' : ''}
                        onClick={() => setViewMode('edit')}
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        className={viewMode === 'split' ? 'active' : ''}
                        onClick={() => setViewMode('split')}
                    >
                        Split
                    </button>
                    <button
                        type="button"
                        className={viewMode === 'preview' ? 'active' : ''}
                        onClick={() => setViewMode('preview')}
                    >
                        Preview
                    </button>
                </div>
                {isDirty && <span style={{ color: '#b45309', fontSize: '0.85rem' }}>Unsaved changes</span>}
            </div>

            <input
                type="text"
                className="admin-legal-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Document title"
                aria-label="Document title"
            />

            <div className={layoutClass}>
                <div className="admin-legal-edit-pane">
                    <textarea
                        className="admin-legal-textarea"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        spellCheck
                        aria-label={`${label} markdown body`}
                    />
                    {hasEsign && (
                        <>
                            <label htmlFor={`esign-${documentKey}`} style={{ display: 'block', marginTop: 12, fontWeight: 600 }}>
                                E-sign notice (shown on accept page)
                            </label>
                            <textarea
                                id={`esign-${documentKey}`}
                                className="admin-legal-esign-input"
                                value={esignNotice}
                                onChange={(e) => setEsignNotice(e.target.value)}
                                rows={3}
                            />
                        </>
                    )}
                </div>
                <div className="admin-legal-preview-pane">
                    <h1>{title || label}</h1>
                    <ReactMarkdown>{body}</ReactMarkdown>
                </div>
            </div>

            <div className="admin-legal-actions">
                <button
                    type="button"
                    className="admin-legal-btn admin-legal-btn-primary"
                    onClick={handleSave}
                    disabled={saving || !isDirty}
                >
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
                <button
                    type="button"
                    className="admin-legal-btn admin-legal-btn-secondary"
                    onClick={handleDiscard}
                    disabled={!isDirty || saving}
                >
                    Discard changes
                </button>
            </div>
            {error && <p className="admin-legal-error">{error}</p>}
            {success && <p className="admin-legal-success">{success}</p>}
        </div>
    );
}

export default function AdminLegalDocuments() {
    const token = useSelector(getToken);
    const [documents, setDocuments] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const loadDocs = useCallback(() => {
        setLoading(true);
        setLoadError('');
        getAdminLegalDocuments(token)
            .then((res) => {
                if (res.data?.success && res.data.documents) {
                    setDocuments(res.data.documents);
                } else {
                    setLoadError('Failed to load legal documents.');
                }
            })
            .catch(() => setLoadError('Failed to load legal documents.'))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        loadDocs();
    }, [loadDocs]);

    const handleDocSaved = useCallback((key, updated) => {
        setDocuments((prev) => ({
            ...prev,
            [key]: { ...updated, isDefault: false }
        }));
    }, []);

    if (loading) {
        return (
            <div className="adminDashboardContainer">
                <div className="adminDashboardScreenPageContainer admin-legal-page">
                    <div className="admin-legal-loading">Loading legal documents…</div>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="adminDashboardContainer">
                <div className="adminDashboardScreenPageContainer admin-legal-page">
                    <p className="admin-legal-error">{loadError}</p>
                    <button type="button" className="admin-legal-btn admin-legal-btn-secondary" onClick={loadDocs}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="adminDashboardContainer">
            <div className="adminDashboardScreenPageContainer admin-legal-page">
                <header className="admin-legal-header">
                    <h1>Legal documents</h1>
                    <p>Edit Terms of Service and the Screen Host Agreement. Markdown is supported.</p>
                </header>

                <Tabs className="admin-legal-tabs">
                    <TabList>
                        {TAB_CONFIG.map((tab) => (
                            <Tab key={tab.key}>{tab.label}</Tab>
                        ))}
                    </TabList>
                    {TAB_CONFIG.map((tab) => (
                        <TabPanel key={tab.key}>
                            <DocumentEditor
                                documentKey={tab.key}
                                label={tab.label}
                                hasEsign={tab.hasEsign}
                                doc={documents?.[tab.key]}
                                onSaved={(updated) => handleDocSaved(tab.key, updated)}
                            />
                        </TabPanel>
                    ))}
                </Tabs>
            </div>
        </div>
    );
}
